import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@foundation/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import styles from './urls.index.module.css'
import { api, type TrackedUrl, type TrackedUrlStatus } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/urls/')({
  component: UrlsIndexPage,
})

const STATUS_BADGE_VARIANT: Record<TrackedUrlStatus, 'success' | 'danger' | 'warning'> = {
  active: 'success',
  error: 'danger',
  paused: 'warning',
}

const statusFilterOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'error', label: 'Error' },
  { value: 'paused', label: 'Paused' },
]

const frequencyOptions = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'six_hour', label: 'Every 6 hours' },
  { value: 'daily', label: 'Daily' },
]

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function UrlsIndexPage() {
  const [urls, setUrls] = useState<TrackedUrl[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')

  // Add URL modal state
  const [showAddModal, setShowAddModal] = useState(false)
  const [newUrl, setNewUrl] = useState('')
  const [newDisplayName, setNewDisplayName] = useState('')
  const [_newFrequency, setNewFrequency] = useState('daily')
  const [isCreating, setIsCreating] = useState(false)

  // Action loading states
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const fetchUrls = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const params: {
        page: number
        limit: number
        status?: TrackedUrlStatus
        search?: string
      } = { page, limit: 10 }
      if (statusFilter) params.status = statusFilter as TrackedUrlStatus
      if (searchDebounced) params.search = searchDebounced
      const result = await api.listTrackedUrls(params)
      setUrls(result.data)
      setTotalPages(result.pagination.totalPages)
      setTotal(result.pagination.total)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load URLs'
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }, [page, statusFilter, searchDebounced])

  useEffect(() => {
    void fetchUrls()
  }, [fetchUrls])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newUrl.trim()) return

    try {
      setIsCreating(true)
      const payload: { url: string; displayName?: string } = {
        url: newUrl.trim(),
      }
      if (newDisplayName.trim()) {
        payload.displayName = newDisplayName.trim()
      }
      await api.createTrackedUrl(payload)
      toast.success('URL added successfully')
      setShowAddModal(false)
      setNewUrl('')
      setNewDisplayName('')
      setNewFrequency('daily')
      void fetchUrls()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add URL')
    } finally {
      setIsCreating(false)
    }
  }

  const handleTogglePause = async (url: TrackedUrl) => {
    try {
      setActionLoadingId(url.id)
      const newStatus = url.status === 'paused' ? 'active' : 'paused'
      await api.updateTrackedUrl(url.id, { status: newStatus })
      toast.success(newStatus === 'paused' ? 'URL paused' : 'URL resumed')
      void fetchUrls()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update URL')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleDelete = async (url: TrackedUrl) => {
    if (
      !window.confirm(
        `Are you sure you want to delete tracking for "${url.displayName || url.url}"?`
      )
    ) {
      return
    }
    try {
      setActionLoadingId(url.id)
      await api.deleteTrackedUrl(url.id)
      toast.success('URL deleted')
      void fetchUrls()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete URL')
    } finally {
      setActionLoadingId(null)
    }
  }

  if (error && urls.length === 0) {
    return (
      <>
        <Alert variant="danger">{error}</Alert>
        <div style={{ marginTop: '1rem' }}>
          <Button onClick={() => void fetchUrls()}>Retry</Button>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Input
            className={styles.searchInput}
            placeholder="Search URLs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            className={styles.statusFilter}
            options={statusFilterOptions}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          Add URL
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <Spinner size="lg" />
          <span>Loading tracked URLs...</span>
        </div>
      ) : urls.length === 0 ? (
        <Card>
          <CardBody>
            <div className={styles.emptyState}>
              <div className={styles.emptyIllustration ?? ''}>
                <svg
                  width="120"
                  height="120"
                  viewBox="0 0 120 120"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="20"
                    y="30"
                    width="80"
                    height="60"
                    rx="8"
                    stroke="var(--text-muted)"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle cx="40" cy="45" r="3" fill="var(--text-muted)" />
                  <circle cx="50" cy="45" r="3" fill="var(--text-muted)" />
                  <circle cx="60" cy="45" r="3" fill="var(--text-muted)" />
                  <line
                    x1="30"
                    y1="55"
                    x2="90"
                    y2="55"
                    stroke="var(--text-muted)"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="35"
                    y="62"
                    width="50"
                    height="4"
                    rx="2"
                    fill="var(--text-muted)"
                    opacity="0.4"
                  />
                  <rect
                    x="35"
                    y="70"
                    width="35"
                    height="4"
                    rx="2"
                    fill="var(--text-muted)"
                    opacity="0.3"
                  />
                  <circle cx="90" cy="80" r="16" fill="var(--color-primary)" opacity="0.15" />
                  <line
                    x1="85"
                    y1="80"
                    x2="95"
                    y2="80"
                    stroke="var(--color-primary)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="90"
                    y1="75"
                    x2="90"
                    y2="85"
                    stroke="var(--color-primary)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h3 className={styles.emptyTitle}>
                {searchDebounced || statusFilter
                  ? 'No URLs match your filters'
                  : 'No URLs tracked yet'}
              </h3>
              <p className={styles.emptyDescription}>
                {searchDebounced || statusFilter
                  ? 'Try adjusting your search or filter to find what you are looking for.'
                  : 'Add a competitor pricing page to start monitoring for changes. You will receive alerts whenever prices are updated.'}
              </p>
              {!searchDebounced && !statusFilter && (
                <Button variant="primary" onClick={() => setShowAddModal(true)}>
                  Track Your First URL
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>URL</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Last Checked</TableHeader>
                  <TableHeader>Last Change</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {urls.map((url) => (
                  <TableRow key={url.id}>
                    <TableCell>
                      <div className={styles.urlCell}>
                        <div className={styles.urlText}>{url.displayName || url.url}</div>
                        {url.displayName && <div className={styles.urlDisplayName}>{url.url}</div>}
                        {url.status === 'error' && url.lastError && (
                          <div className={styles.errorText} title={url.lastError}>
                            {url.lastError}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={STATUS_BADGE_VARIANT[url.status]}
                        className={styles.statusBadge}
                      >
                        {url.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className={styles.timestampCell}>{formatDate(url.lastCheckedAt)}</span>
                    </TableCell>
                    <TableCell>
                      <span className={styles.timestampCell}>{formatDate(url.lastChangeAt)}</span>
                    </TableCell>
                    <TableCell>
                      <div className={styles.actions}>
                        <Link to="/urls/$id" params={{ id: url.id }}>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleTogglePause(url)}
                          disabled={actionLoadingId === url.id}
                        >
                          {url.status === 'paused' ? 'Resume' : 'Pause'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleDelete(url)}
                          disabled={actionLoadingId === url.id}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                Page {page} of {totalPages} ({total} total)
              </span>
              <div className={styles.paginationButtons}>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add URL Modal */}
      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Tracked URL"
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={isCreating}
              onClick={(e) => void handleCreate(e as unknown as React.FormEvent)}
              form="add-url-form"
              type="submit"
            >
              Add URL
            </Button>
          </div>
        }
      >
        <form id="add-url-form" className={styles.modalForm} onSubmit={(e) => void handleCreate(e)}>
          <Input
            label="URL"
            type="url"
            placeholder="https://example.com/pricing"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            required
          />
          <Input
            label="Display Name (optional)"
            type="text"
            placeholder="e.g., Competitor A Pricing"
            value={newDisplayName}
            onChange={(e) => setNewDisplayName(e.target.value)}
          />
          <Select
            label="Check Frequency"
            options={frequencyOptions}
            value={_newFrequency}
            onChange={(e) => setNewFrequency(e.target.value)}
          />
        </form>
      </Modal>
    </>
  )
}
